import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import moment from "moment-timezone";
import { Box } from "theme-ui";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import SelectDate from "components/forms/SelectDate";
import Subtext from "components/typography/Subtext";
import { CheckMarkIcon } from "components/svg";
import { themevals } from "theme/themevals";
import { useToast } from "components/toast";

import { createPlanDirectQuote, getInstantEstimateProducts } from "api/ServicePlans";

import type { EnrichedSite } from "./planRosterTypes";

/**
 * OPERATING_MODEL.md — the quote-fed "issue a quote directly at a
 * location" action. Church / HOA / most clients have no work orders;
 * staff picks a location already on the plan, and a plan-tagged RFQ is
 * produced (seeded from that location's LCP) so they continue in the
 * normal trunk RFQ UI. See design/DIRECT_QUOTE_QUOTE_FED.md.
 *
 * Deliberately small: location (required), optional target date,
 * optional note. No service selection here — that happens in the RFQ
 * UI on the next screen, same as the WO convert flow. On success we
 * navigate straight to /quote-requests/:rfqId.
 *
 * `preselectedLcpId` lets a row-level "New quote" action open this
 * modal with the location already chosen (used when the direct-quote
 * action is triggered from a specific Location row rather than the
 * page header).
 */
export default function NewDirectQuoteModal({
  planId,
  planName,
  sites,
  sitesLoading,
  preselectedLcpId,
  onClose,
}: {
  planId: string;
  planName?: string;
  sites: EnrichedSite[];
  /** True while the plan's Location list is still being fetched (it is
   * loaded on demand, being unpaged and large on big plans). Keeps the
   * picker from reading as "this plan has no locations". */
  sitesLoading?: boolean;
  preselectedLcpId?: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  // §6.1 — the shared Instant-Estimate product source. When staff pick a
  // product, the quote prices in one step through the same engine a
  // normal RFQ uses (real-time, location-aware). When they leave it on
  // "No product," the legacy bare-RFQ path runs and services get picked
  // on the next screen. Defaulted to the primary product (highest real
  // coverage, index 0) so the common case is one tap. null = loading;
  // [] = loaded, none available (picker hidden, bare path).
  const [products, setProducts] = useState<any[] | null>(null);

  useEffect(() => {
    let alive = true;
    getInstantEstimateProducts().then((rows: any[]) => {
      if (alive) setProducts(Array.isArray(rows) ? rows : []);
    });
    return () => {
      alive = false;
    };
  }, []);

  const productOptions = useMemo(
    () => [
      { value: "", label: "No product (pick services on next screen)" },
      ...(products || []).map((p: any) => ({
        value: p.id,
        label: p.name,
      })),
    ],
    [products],
  );

  const primaryProductId = products && products.length > 0 ? products[0].id : "";

  // Build the location options from the plan's sites. A quote-fed plan
  // issues quotes only at locations already on the plan — the same
  // constraint the backend enforces — so there's no free-text add here.
  // Adding a brand-new location uses the existing "Add Location" action
  // first, then this modal.
  const locationOptions = useMemo(
    () =>
      (sites || [])
        .filter((s) => !!s.lcpId)
        .map((s) => {
          const name = s.lcpName || s.address?.line1 || "(Unnamed Location)";
          const city = [s.address?.city, s.address?.state].filter(Boolean).join(", ");
          return {
            value: s.lcpId,
            label: city ? `${name} — ${city}` : name,
          };
        }),
    [sites],
  );

  const _validate = Yup.object().shape({
    lcpId: Yup.string().required("Pick a location."),
  });

  const submit = async (values: any) => {
    setSubmitting(true);
    const res = await createPlanDirectQuote({
      data: {
        planId,
        lcpId: values.lcpId,
        serviceDate: values.serviceDate
          ? moment.unix(values.serviceDate).format("YYYY-MM-DD")
          : undefined,
        instructions: values.instructions || undefined,
        productId: values.productId || undefined,
      },
    });
    setSubmitting(false);

    if (res?.rfqId) {
      toast.add(
        values.productId ? "Quote priced and started." : "Quote started.",
        "var(--theme-ui-colors-success)",
      );
      onClose();
      navigate(`/quote-requests/${res.rfqId}`);
    } else {
      toast.add(
        res?.message ? res.message : "Error starting quote.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  return (
    <Modal isOpen={true} toggle={onClose} size="md" centered>
      <ModalHeader toggle={onClose}>
        New Quote{planName ? ` · ${planName}` : ""}
      </ModalHeader>
      <Formik
        enableReinitialize
        initialValues={{
          lcpId: preselectedLcpId || "",
          productId: primaryProductId,
          serviceDate: undefined,
          instructions: "",
        }}
        validationSchema={_validate}
        onSubmit={submit}
      >
        {({ values, errors, submitCount, setFieldValue }) => (
          <Form noValidate autoComplete="off">
            <ModalBody>
              <Subtext
                fontSize={"12px"}
                fontFamily={"Poppins"}
                margin={0}
                marginBottom={"14px"}
                color={themevals.colors.dkgrey}
              >
                Issue a quote directly at one of this plan&apos;s locations. You&apos;ll pick services and generate the quote on the next screen, then send it for the client to accept.
              </Subtext>

              <Box sx={{ marginBottom: "18px" }}>
                <SelectInput
                  name="lcpId"
                  label="Location"
                  required
                  value={values.lcpId}
                  options={locationOptions}
                  onChange={(v: any) => setFieldValue("lcpId", v || "")}
                  $errors={errors.lcpId && submitCount > 0 ? errors.lcpId : null}
                  searchable
                />
                {locationOptions.length === 0 && (
                  <Subtext
                    fontSize={"11px"}
                    fontFamily={"Poppins"}
                    margin={0}
                    marginTop={"6px"}
                    color={themevals.colors.dkgrey}
                  >
                    {sitesLoading
                      ? "Loading this plan\u2019s locations\u2026"
                      : "Add a location to this plan first with \u201cAdd Location\u201d above."}
                  </Subtext>
                )}
              </Box>

              {products !== null && products.length > 0 && (
                <Box sx={{ marginBottom: "18px" }}>
                  <SelectInput
                    name="productId"
                    label="Product"
                    value={values.productId}
                    options={productOptions}
                    onChange={(v: any) => setFieldValue("productId", v || "")}
                    searchable={productOptions.length > 8}
                    clearable={false}
                  />
                  <Subtext
                    fontSize={"11px"}
                    fontFamily={"Poppins"}
                    margin={0}
                    marginTop={"6px"}
                    color={themevals.colors.dkgrey}
                  >
                    {values.productId
                      ? "The quote is priced in real time from this product and the location, same as an Instant Estimate. You can adjust it on the next screen."
                      : "No product selected — you'll choose services and price on the next screen."}
                  </Subtext>
                </Box>
              )}

              <Box sx={{ marginBottom: "18px" }}>
                <SelectDate
                  name="serviceDate"
                  label="Target Service Date (optional)"
                  value={values.serviceDate}
                  onChange={(v: any) => setFieldValue("serviceDate", v)}
                />
              </Box>

              <TextInput
                type="textarea"
                name="instructions"
                label="Note (optional)"
                rows={3}
                value={values.instructions}
                onChange={(v: any) => setFieldValue("instructions", v)}
                placeholder="Anything the quote should carry — scope notes, access details, etc."
              />
            </ModalBody>
            <ModalFooter>
              <BasicButton type="button" outline scheme="secondary" onClick={onClose}>
                Cancel
              </BasicButton>
              <BasicButton
                type="submit"
                $submitting={submitting}
                $iconHeight="14px"
                $iconWidth="14px"
                $loadingColor="#fff"
                keepText={true}
                disabled={locationOptions.length === 0 || !!sitesLoading}
              >
                {!submitting && <CheckMarkIcon fill={"#fff"} />}
                Start Quote
              </BasicButton>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
